import { ChevronDown, ChevronRight } from 'lucide-react';
import { Fragment, type ReactNode } from 'react';

import { type TicketListItem } from '../../services/api/tickets';
import { cn } from '../../utils/cn';
import { formatDateTime } from '../../utils/datetime';
import { SEVERITY_COLORS, SEVERITY_LABELS } from '../../utils/severity-labels';

import { TICKET_SOURCE_LABELS, TICKET_STATUS_COLORS, TICKET_STATUS_LABELS } from './ticket-labels';

const Badge = ({ className, children }: { className: string; children: ReactNode }) => (
  <span className={cn('inline-block rounded-sm border px-2 py-0.5 text-xs', className)}>
    {children}
  </span>
);

const TicketRow = ({
  item,
  expanded,
  onToggle,
}: {
  item: TicketListItem;
  expanded: boolean;
  onToggle: () => void;
}) => (
  <tr
    onClick={onToggle}
    className={cn('cursor-pointer transition hover:bg-slate-50', expanded && 'bg-slate-50')}
  >
    <td className="px-3 py-2 text-slate-400">
      {expanded ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
    </td>
    <td className="px-3 py-2 whitespace-nowrap text-slate-600">{formatDateTime(item.createdAt)}</td>
    <td className="px-3 py-2 font-medium text-slate-900">{item.title}</td>
    <td className="px-3 py-2">
      <Badge className="border-slate-200 bg-slate-50 text-slate-600">
        {TICKET_SOURCE_LABELS[item.source]}
      </Badge>
    </td>
    <td className="px-3 py-2 text-slate-600">{item.sessionIds.length}</td>
    <td className="px-3 py-2">
      <Badge className={cn('font-medium', SEVERITY_COLORS[item.severity])}>
        {SEVERITY_LABELS[item.severity]}
      </Badge>
    </td>
    <td className="px-3 py-2">
      <Badge className={cn('font-medium', TICKET_STATUS_COLORS[item.status])}>
        {TICKET_STATUS_LABELS[item.status]}
      </Badge>
    </td>
  </tr>
);

interface TicketTableProps {
  items: TicketListItem[];
  expandedId: string | null;
  onToggle: (id: string) => void;
  renderDetail: (item: TicketListItem) => ReactNode;
}

export const TicketTable = ({ items, expandedId, onToggle, renderDetail }: TicketTableProps) => (
  <div className="overflow-x-auto rounded-lg border border-slate-200">
    <table className="min-w-full divide-y divide-slate-200 text-sm">
      <thead className="bg-brand-beige-tint-1">
        <tr className="text-left text-xs uppercase tracking-wide text-slate-600">
          <th className="px-3 py-2 w-8" />
          <th className="px-3 py-2">Skapad</th>
          <th className="px-3 py-2">Titel</th>
          <th className="px-3 py-2">Källa</th>
          <th className="px-3 py-2">Sessioner</th>
          <th className="px-3 py-2">Allvarlighet</th>
          <th className="px-3 py-2">Status</th>
        </tr>
      </thead>
      <tbody className="divide-y divide-slate-100 bg-white">
        {items.map((item) => {
          const expanded = expandedId === item.id;
          return (
            <Fragment key={item.id}>
              <TicketRow item={item} expanded={expanded} onToggle={() => onToggle(item.id)} />
              {expanded && (
                <tr>
                  <td colSpan={7} className="bg-slate-50/60 px-4 py-4">
                    {renderDetail(item)}
                  </td>
                </tr>
              )}
            </Fragment>
          );
        })}
      </tbody>
    </table>
  </div>
);
